/**
 * User-facing labels for the internal vocabulary the API returns.
 *
 * The API speaks in stable identifiers (`managed_docker`, `commerceops_docker`,
 * `magento_onboarding`, `waiting_for_agent`, …). Those are contracts, not
 * copy: every screen that shows one of them to a person goes through this
 * module so the same concept always has the same name. The canonical terms
 * and the reasoning behind them live in docs/UX_TERMINOLOGY.md — keep the
 * two in step.
 */
import type {
  AutoIssueStatus,
  RemediationActionState,
  RemediationRisk,
  ApplicationCapability,
  CapabilityDetectionStatus,
  CapabilityStatus,
  CertificateStatus,
  ContinuousMonitorStatus,
  DeploymentGroupStatus,
  DeploymentMode,
  DeploymentStatus,
  DeploymentStrategy,
  ErrorGroupStatus,
  ExecutionMode,
  IncidentLifecycleStatus,
  InvitationStatus,
  JobStatus,
  PreflightStatus,
  ReadinessCheckStatus,
  RecoveryPointStatus,
  VarnishState,
  ResourceOwnership,
  ResourceStatus,
  RestoreRunStatus,
  RestoreTestStatus,
  RiskLevel,
  RollbackPolicy,
  ServerReadinessStatus,
  ServerStatus,
  StoreMigrationStatus,
  SubscriptionStatus,
  VerificationStatus,
} from './api';

// ---------------------------------------------------------------------------
// Environments
// ---------------------------------------------------------------------------

/**
 * How an environment is deployed. `managed_docker` is the current,
 * agent-driven path (Commerce Ops runs the Magento stack in Docker on the
 * server); `existing_atomic` is the SSH path for a Magento installation the
 * customer already runs natively on the server.
 */
export const deploymentModeLabel: Record<DeploymentMode, string> = {
  managed_docker: 'Commerce Ops managed stack',
  existing_atomic: 'Existing Magento server (SSH)',
};

export const deploymentModeDescription: Record<DeploymentMode, string> = {
  managed_docker:
    'Commerce Ops runs Magento, its database, cache and — only when the code needs one — a search service as Docker containers on the server, through the Commerce Ops Agent. Recommended.',
  existing_atomic:
    'Magento already runs natively on the server. Commerce Ops connects over SSH and switches releases atomically; it does not change the server’s own PHP, database or web server.',
};

export const rollbackPolicyLabel: Record<RollbackPolicy, string> = {
  automatic_code_rollback_when_safe: 'Roll back automatically when the database allows',
  manual_recovery: 'Never roll back automatically',
};

export const rollbackPolicyDescription: Record<RollbackPolicy, string> = {
  automatic_code_rollback_when_safe:
    'After a failed deployment Commerce Ops switches back to the previous release by itself — but only when the database change of the failed release is known to be compatible with it.',
  manual_recovery:
    'A failed deployment stays as it is until someone decides what to do. Nothing is switched back automatically.',
};

/**
 * What stops blue-green runtimes on an environment, by the code the server
 * reports (the eligibility every blue-green deployment plan judges). The items
 * of a blocker (agent commands, the ingress in use, storefronts) are listed
 * beside the label.
 */
export const eligibilityBlockerLabel: Record<string, string> = {
  agent_not_connected: 'Agent not connected',
  agent_commands_missing: 'Agent must be upgraded',
  ingress_not_commerce_ops: 'Ingress is not the Commerce Ops edge',
  frontend_backend_probe_missing: 'No backend readiness route',
  stack_unreadable: 'Stack not readable',
};

export const environmentKindLabel: Record<string, string> = {
  production: 'Production',
  staging: 'Staging',
  development: 'Development',
  preview: 'Preview',
};

// ---------------------------------------------------------------------------
// Deployment strategies (onboarding plan)
// ---------------------------------------------------------------------------

export const strategyLabel: Record<DeploymentStrategy, string> = {
  commerceops_docker: 'Commerce Ops managed stack',
  atomic_native: 'Atomic releases on the server’s own stack (SSH)',
  existing_native: 'In place on the server’s own stack (SSH)',
  existing_docker: 'Your existing Docker Compose stack',
};

export const strategyDescription: Record<DeploymentStrategy, string> = {
  commerceops_docker:
    'Commerce Ops installs and operates Magento, MariaDB, Redis and, when the code needs it, a search service (OpenSearch or Elasticsearch) as isolated containers through the agent. Releases, backups, monitoring and rollback are all handled for you; with blue-green runtimes adopted, code-only releases switch without a maintenance window.',
  atomic_native:
    'Uses the PHP, Composer, database and web server already on the server. Releases are switched atomically, but this path runs over an SSH connection you configure — the agent does not perform it.',
  existing_native:
    'Deploys into the server’s existing Magento directory as it is. No atomic switch, so a failed deployment cannot be rolled back automatically. Runs over SSH.',
  existing_docker:
    'Would reuse a docker-compose.yml from your repository. Not available yet.',
};

export const capabilityStatusLabel: Record<CapabilityStatus, string> = {
  recommended: 'Recommended',
  available: 'Available',
  supported: 'Supported',
  supported_with_warning: 'Supported with warnings',
  blocked: 'Not possible on this server',
  // The server check ran and did not find it (or could not measure it);
  // "Not checked yet" is reserved for a check that has not run.
  unknown: 'Not detected',
};

// ---------------------------------------------------------------------------
// Servers and the agent
// ---------------------------------------------------------------------------

/** The agent connection state — deliberately about the agent, not "the
 * server is healthy": a connected agent says nothing about the store. */
export const serverStatusLabel: Record<ServerStatus, string> = {
  online: 'Agent connected',
  offline: 'Agent offline',
  waiting_for_agent: 'Waiting for agent',
  error: 'Error',
};

export const readinessStatusLabel: Record<ServerReadinessStatus, string> = {
  not_run: 'Not checked yet',
  running: 'Checking…',
  ready: 'Ready',
  warning: 'Ready with warnings',
  failed: 'Not ready',
};

export const ownershipLabel: Record<ResourceOwnership, string> = {
  commerceops: 'Managed by Commerce Ops',
  external: 'External',
  detected: 'Found on the server',
  unknown: 'Unknown',
};

export const ownershipDescription: Record<ResourceOwnership, string> = {
  commerceops: 'Created and operated by Commerce Ops; may be restarted and upgraded automatically.',
  external: 'Runs elsewhere. Commerce Ops connects to it and never changes it.',
  detected: 'Found on the server. Commerce Ops uses it but does not manage it.',
  unknown: 'Ownership has not been established; nothing is done to it automatically.',
};

export const resourceStatusLabel: Record<ResourceStatus, string> = {
  reachable: 'Reachable',
  unreachable: 'Unreachable',
  pending: 'Not started yet',
  unknown: 'Unknown',
};

// ---------------------------------------------------------------------------
// Executions, deployments and workflows
// ---------------------------------------------------------------------------

export const executionModeLabel: Record<ExecutionMode, string> = {
  // "Live" names a serving release only (CO-150); a run that really changes
  // the server is a real run, as opposed to a rehearsal.
  live: 'Real run',
  rehearsal: 'Rehearsal',
};

/** Workflow types of an execution. The onboarding workflow is what users
 * see as "setting up the environment on the server". */
export const workflowLabel: Record<string, string> = {
  magento_onboarding: 'Environment setup',
  onboarding: 'Environment setup',
  deployment: 'Deployment',
  frontend_deployment: 'Frontend deployment',
  backup: 'Backup',
  restore: 'Restore',
  rollback: 'Rollback',
  recovery: 'Restore test',
  remediation: 'Fix',
  existing_store_migration: 'Store migration',
  wordpress_patch: 'Update run',
};

export const executionStatusLabel: Record<string, string> = {
  pending: 'Not started',
  preflighting: 'Checking prerequisites',
  running: 'Running',
  paused: 'Paused',
  paused_waiting_for_agent: 'Waiting for the agent to reconnect',
  waiting_for_input: 'Needs you',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  rolled_back: 'Rolled back',
};

/** Where a setup plan stands, from generation to its setup run (CO-73).
 * The API derives it from the plan's review status and its latest run. */
export const planLifecycleLabel: Record<string, string> = {
  draft: 'Draft',
  needs_input: 'Needs information',
  blocked: 'Not possible on this server',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved — not run yet',
  run_pending: 'Run created — not started',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  superseded: 'Superseded',
};


/** What is in flight when a project cannot be deleted yet (CO-74). */
export const projectDeletionBlockerLabel: Record<string, string> = {
  run: 'Run on the server',
  deployment: 'Deployment',
  coordinated_deployment: 'Coordinated deployment',
  legacy_deployment: 'Deployment (SSH)',
  component_removal: 'Component being removed',
};

export const projectDeploymentStatusLabel: Record<string, string> = {
  completed_with_failures: 'Partially succeeded',
  pending: 'Not started',
  queued: 'Queued',
  preflighting: 'Checking prerequisites',
  building: 'Building',
  deploying: 'Deploying',
  verifying: 'Verifying',
  activating: 'Switching traffic',
  completed: 'Succeeded',
  succeeded: 'Succeeded',
  failed: 'Failed',
  rolled_back: 'Rolled back',
  cancelled: 'Cancelled',
  awaiting_db_approval: 'Waiting for database approval',
};

export const dbChangeLabel: Record<string, string> = {
  no_db_change: 'No database change',
  db_change_backward_compatible: 'Compatible database change',
  db_change_detected: 'Database change',
  db_change_destructive: 'Destructive database change',
  unknown: 'Not analysed',
};

// ---------------------------------------------------------------------------
// Applications
// ---------------------------------------------------------------------------

/** Why a recovery point was taken (domain.RecoveryPointType). */
/** What a guarded fix does, in words; the engine id stays as secondary text (UX-12). */
export const remediationActionTypeLabel: Record<string, string> = {
  'health_check.rerun': 'Run the check again',
  'queue_consumer.restart': 'Restart the queue consumer',
  'managed_service.restart': 'Restart the service',
  'backup.retry': 'Retry the backup',
  'deployment.rollback': 'Roll back the deployment',
  'magento.cache_clean': 'Clean the Magento cache',
  'magento.cache_flush': 'Flush the Magento cache',
  'varnish.vcl_reload': 'Reload the Varnish configuration',
};

/** The kind of thing a fix acts on. */
export const remediationTargetTypeLabel: Record<string, string> = {
  consumer: 'Queue consumer',
  queue_consumer: 'Queue consumer',
  service: 'Service',
  managed_service: 'Service',
  deployment: 'Deployment',
  monitor: 'Check',
  backup: 'Backup',
  application: 'Component',
};

/** Who runs the target: only what Commerce Ops runs is fixed automatically. */
export const resourceOwnershipLabel: Record<string, string> = {
  commerceops: 'Commerce Ops',
  external: 'Your team, outside Commerce Ops',
  detected: 'Found on the server, not run by Commerce Ops',
  unknown: 'Not known',
};

/** Whether a step can be undone, in words (UX-12); "Reversibility: Safe" said nothing. */
export const stepReversibilityLabel: Record<string, string> = {
  safe: 'Changes nothing that needs undoing',
  reversible: 'Can be undone',
  conditionally_reversible: 'Can be undone in some cases',
  compensatable: 'Undone by a follow-up step',
  irreversible: 'Cannot be undone',
};

export const recoveryPointTypeLabel: Record<string, string> = {
  scheduled: 'Scheduled',
  manual: 'Taken by hand',
  pre_deployment: 'Before a deployment',
  pre_upgrade: 'Before an upgrade',
  pre_restore: 'Before a restore',
};

export const applicationTypeLabel: Record<string, string> = {
  magento_backend: 'Magento',
  wordpress: 'WordPress',
  frontend: 'Frontend',
  frontend_storefront: 'Frontend',
};

// ---------------------------------------------------------------------------
// Deployable components (CO-48)
// ---------------------------------------------------------------------------

/** The rolled-up health of one component in one environment. */
export const componentHealthLabel: Record<string, string> = {
  healthy: 'Healthy',
  warning: 'Needs attention',
  critical: 'Down',
  unknown: 'Not checked yet',
  disabled: 'Disabled',
  not_monitored: 'Not monitored',
};

/** The outcome of a coordinated deployment (Deploy all / Deploy selected). */
export const deploymentGroupStatusLabel: Record<string, string> = {
  pending: 'Not started',
  running: 'In progress',
  completed: 'All live',
  completed_with_failures: 'Partly live',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** Where a component's pages are cached in front of it (CO-49). */
export const cacheProviderLabel: Record<string, string> = {
  none: 'No external cache',
  varnish: 'Varnish',
  cloudflare: 'Cloudflare',
  custom_endpoint: 'Purge endpoint',
};

/** How much of a component's cache a release invalidates. */
export const cachePurgeStrategyLabel: Record<string, string> = {
  none: 'Never purge automatically',
  changed_urls: 'The URLs listed below',
  full_component: 'Everything on this storefront',
};

/** Whether a Magento release invalidates a storefront's cache. */
export const dependentCachePolicyLabel: Record<string, string> = {
  never: 'Leave the cache alone',
  always: 'Invalidate every time',
};

/** Where a component is between existing and being gone. */
export const componentLifecycleLabel: Record<string, string> = {
  active: 'Active',
  removing: 'Removing…',
  removal_failed: 'Removal failed',
  removed: 'Removed',
};

/** What a deployment covered. */
export const deploymentScopeLabel: Record<string, string> = {
  magento_only: 'Magento',
  frontend_only: 'One frontend',
  combined: 'Magento and frontend',
  selected: 'Selected components',
  all: 'All components',
};

// ---------------------------------------------------------------------------
// Setup plan: repository analysis
// ---------------------------------------------------------------------------

/** Where the plan's repository analysis came from. */
export const repositoryAnalysisSourceLabel: Record<string, string> = {
  repository: 'Analyzed from repository',
  assumed_defaults: 'Using assumed defaults',
};

/** Why the repository was not read. The API also sends a full sentence
 * (`fallback_message`); these are the short forms for badges and lists. */
export const repositoryFallbackReasonLabel: Record<string, string> = {
  repository_url_not_configured: 'No repository URL',
  repository_host_unsupported: 'Repository host not supported for reading files',
  repository_access_not_configured: 'Repository access not configured',
  branch_not_found: 'Branch not found',
};

// ---------------------------------------------------------------------------
// Servers: why a server cannot be removed
// ---------------------------------------------------------------------------

/** Why an environment counts as still using a server (`server_in_use`). */
export const serverDependencyReasonLabel: Record<string, string> = {
  placed: 'Placed on this server',
  run_in_progress: 'A run is in progress',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** humanize is the fallback for identifiers that have no curated label yet:
 * `some_internal_value` → `Some internal value`. */
// ---------------------------------------------------------------------------
// Magento multi-site topology (CO-46): websites, store views, domains,
// frontend bindings. See docs/UX_TERMINOLOGY.md § Storefronts.
// ---------------------------------------------------------------------------

/** What a hostname is for. */
export const domainPurposeLabel: Record<string, string> = {
  magento_storefront: 'Storefront (Magento)',
  headless_storefront: 'Storefront (frontend)',
  backend_api: 'Magento API',
  admin: 'Magento Admin',
  media: 'Media',
  alias: 'Redirect alias',
};

export const domainPurposeDescription: Record<string, string> = {
  magento_storefront: 'Magento renders the shop on this hostname; the web server routes it to the website or store view below.',
  headless_storefront: 'A frontend deployment answers on this hostname and talks to Magento through its API.',
  backend_api: 'The hostname frontends call for GraphQL and REST. Not a public shop.',
  admin: 'Reserved for the Magento Admin.',
  media: 'Serves product images and static files.',
  alias: 'Answers, then redirects to the canonical hostname of the same website (for example www → apex).',
};

export const dnsStatusLabel: Record<string, string> = {
  unknown: 'Not checked yet',
  ok: 'Points at the server',
  missing: 'No DNS record',
  mismatch: 'Points elsewhere',
  proxied: 'Through a CDN',
};

export const tlsObservedLabel: Record<string, string> = {
  unknown: 'Not checked yet',
  valid: 'Valid certificate',
  invalid: 'Certificate not trusted',
  expired: 'Certificate expired',
  hostname_mismatch: 'Certificate for another hostname',
  missing: 'No HTTPS',
  not_applicable: 'HTTP only',
};

export const domainVerificationLabel: Record<string, string> = {
  unknown: 'Not verified',
  passed: 'Verified',
  failed: 'Failing',
  warning: 'Needs attention',
};

export const redirectCheckLabel: Record<string, string> = {
  ok: 'Redirects correctly',
  missing: 'No redirect',
  loop: 'Redirect loop',
  wrong_target: 'Redirects to the wrong hostname',
  error: 'Could not check',
  not_applicable: 'Not applicable',
};

export const routeDestinationLabel: Record<string, string> = {
  magento: 'Magento storefront',
  frontend: 'Frontend',
  redirect: 'Redirect',
  backend: 'Magento backend',
};

/** One difference between Magento and what Commerce Ops has on record. */
export const topologyChangeLabel: Record<string, string> = {
  website_added: 'Website added in Magento',
  website_removed: 'Website no longer in Magento',
  website_code_changed: 'Website code changed',
  website_renamed: 'Website renamed',
  default_website_changed: 'Default website changed',
  store_added: 'Store added',
  store_removed: 'Store removed',
  store_changed: 'Store changed',
  store_view_added: 'Store view added in Magento',
  store_view_removed: 'Store view no longer in Magento',
  store_view_disabled: 'Store view disabled',
  store_view_enabled: 'Store view enabled',
  store_view_code_changed: 'Store view code changed',
  store_view_renamed: 'Store view renamed',
  store_view_moved: 'Store view moved to another website',
  base_url_changed: 'Base URL changed in Magento',
  locale_changed: 'Locale changed',
  frontend_binding_broken: 'Frontend no longer matches Magento',
  domain_binding_broken: 'Hostname no longer matches Magento',
};

export const verificationScopeLabel: Record<string, string> = {
  installation: 'Magento backend',
  website: 'Website',
  store_view: 'Store view',
  frontend: 'Frontend',
  domain: 'Hostname',
};

export const verificationCheckLabel: Record<string, string> = {
  http: 'Storefront responds',
  graphql: 'GraphQL responds',
  graphql_store: 'Right store view',
  catalog: 'Catalog query',
  indexers: 'Indexers',
  frontend: 'Frontend responds',
  redirect: 'Redirect',
  storefront_render: 'Pages, CSS and scripts load',
  catalog_search: 'Catalog search',
  dependencies: 'Redis and message queue',
  search_service: 'Search service',
};

/** Magento indexer states and indexing modes, in Magento's admin words. */
export const indexerStateLabel: Record<string, string> = {
  valid: 'Valid',
  invalid: 'Invalid',
  working: 'Working',
};

export const indexerModeLabel: Record<string, string> = {
  schedule: 'Update by schedule',
  save: 'Update on save',
};

export const catalogSearchPolicyLabel: Record<'warn' | 'block', string> = {
  warn: 'Warn, still promote',
  block: 'Block the deployment',
};

export const siteHealthLabel: Record<string, string> = {
  healthy: 'Healthy',
  warning: 'Needs attention',
  critical: 'Failing',
  unknown: 'Not checked yet',
  disabled: 'Paused',
};

export const cacheLayerLabel: Record<string, string> = {
  magento_application: 'Magento cache',
  full_page_cache: 'Full page cache',
  cdn: 'CDN / edge cache',
  frontend: 'Frontend cache',
};

export const cacheActionLabel: Record<string, string> = {
  flushed_by_deployment: 'flushed by the deployment',
  operator_purge_required: 'purge it yourself after the deployment',
  purged_by_deployment: 'purged by the deployment',
  recreated_by_deployment: 'rebuilt by the deployment',
};

export const ingressOwnerLabel: Record<string, string> = {
  commerceops_gate: 'Commerce Ops gate',
  docker_traefik: 'Traefik (Docker)',
  docker_caddy: 'Caddy (Docker)',
  docker_nginx: 'nginx (Docker, not Commerce Ops)',
  docker_apache: 'Apache (Docker)',
  docker_other: 'Another container',
  host_nginx: 'nginx on the host',
  host_apache: 'Apache on the host',
  host_traefik: 'Traefik on the host',
  host_caddy: 'Caddy on the host',
  none: 'Nothing listens on 80/443',
  unknown: 'Unknown',
};

/** plural: "1 warning", "2 warnings", "0 warnings". */
/** What an attention item is about (domain.AttentionCategory, CO-150). */
export const attentionCategoryLabel: Record<string, string> = {
  site: 'Live site',
  deployment: 'Deployment updates',
  infrastructure: 'Infrastructure',
  recovery: 'Backups and recovery',
  security: 'Security',
  setup: 'Setup and configuration',
  operations: 'Operations',
};

/** The order categories are listed in: what visitors get comes first. */
export const attentionCategoryOrder = ['site', 'deployment', 'infrastructure', 'recovery', 'security', 'setup', 'operations'] as const;

export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const words = value.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** labelFor looks a value up in a label map and falls back to humanize. */
export function labelFor(map: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '';
  return map[value] ?? humanize(value);
}

// ---------------------------------------------------------------------------
// Application capabilities (catalog search)
// ---------------------------------------------------------------------------

/** How sure the analysis is. "Confirmed" means the running store reported
 * the same engine the repository analysis found. */
export const capabilityConfidenceLabel: Record<string, string> = {
  confirmed: 'Confirmed by the running store',
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
  unknown: 'Not determined',
};

/** Who provides the catalog search engine. */
export const searchImplementationLabel: Record<string, string> = {
  magento_core: 'Magento’s own module',
  third_party_module: 'Third-party Magento module',
  application_module: 'Module in this repository',
  unknown: 'Not identified',
};

export const nativeSupportLabel: Record<string, string> = {
  yes: 'Yes',
  deprecated: 'Deprecated on this release',
  no: 'No',
  unknown: 'Not checked',
};

export const repositorySupportLabel: Record<string, string> = {
  detected: 'Detected',
  not_found: 'Provided by Magento itself',
  unknown: 'Not checked',
};

export const compatibilityLabel: Record<string, string> = {
  compatible: 'Compatible with the current project configuration',
  deprecated: 'Works, but deprecated on this Magento release',
  unsupported: 'Not supported on this Magento release',
  unknown: 'Not determined',
};

/** The infrastructure consequence of the verdict (plan component
 * details.decision). */
export const searchDecisionLabel: Record<string, string> = {
  provision: 'Provision a search service',
  reuse: 'Reuse the search service on the server',
  integrate: 'Connect to your external search service',
  not_required: 'No search service needed',
  paused: 'Search provisioning paused',
  operator_override: 'Operator override applied',
};

export const capabilityEvidenceSourceLabel: Record<string, string> = {
  composer_json: 'composer.json',
  composer_lock: 'composer.lock',
  config_php: 'app/etc/config.php',
  env_php: 'app/etc/env.php',
  di_xml: 'module di.xml',
  search_engine_xml: 'module search_engine.xml',
  docker_compose: 'docker-compose.yml',
  magento_cloud: 'Adobe Commerce Cloud services',
  magento_release: 'Magento release',
  runtime_config: 'running store (config:show)',
  runtime_module: 'running store (modules)',
  assumed_defaults: 'repository not read',
  operator: 'set by an operator',
};

/** capabilityStatusLabelFor names the verdict class for a badge. */
export function capabilityStatusLabelFor(cap: ApplicationCapability): string {
  switch (cap.status) {
    case 'conflict':
      return 'Store and repository disagree';
    case 'unresolved':
      return cap.engine_identifier ? 'Custom engine, not identified' : 'Not determined';
    default:
      return 'Detected';
  }
}

// ---------------------------------------------------------------------------
// Value sources (CO-52)
// ---------------------------------------------------------------------------

/**
 * The four families a Commerce Ops value belongs to. The API groups sources
 * by what they describe, not by where they are stored.
 */
export const valueSourceGroupLabel: Record<string, string> = {
  magento: 'Magento',
  frontend: 'Storefront',
  environment: 'Environment',
  component: 'Component',
};

/**
 * valueSourceLabel names a source for a person: `magento.store_view_code`
 * reads as "Magento → Store view code". The source id itself is the API
 * contract and is never shown on its own.
 */
export function valueSourceLabel(source: string): string {
  if (!source || source === 'literal') return 'Manual value';
  const [group = source, ...rest] = source.split('.');
  const name = rest.join('.').replace(/_/g, ' ').replace(/\bgraphql\b/gi, 'GraphQL').replace(/\burl\b/gi, 'URL');
  const label = valueSourceGroupLabel[group] ?? group;
  if (!name) return label;
  return `${label} → ${name.charAt(0).toUpperCase()}${name.slice(1)}`;
}

/**
 * What a build produces: a server that runs, or files that are served.
 * The identifiers name the shape of the output, never the tool that made
 * it — every JavaScript framework can produce either (CO-52).
 */
export const outputRuntimeLabels: Record<string, string> = {
  node_server: 'Node server',
  static_files: 'Static files',
  php_fpm: 'PHP-FPM',
};

export function outputRuntimeLabel(value: string): string {
  return outputRuntimeLabels[value] ?? value;
}

// ---------------------------------------------------------------------------
// Subscriptions and the staff console
// ---------------------------------------------------------------------------

/** A Stripe subscription's state, as the Billing page and the staff console
 * both show it. */
export const subscriptionStatusLabel: Record<string, string> = {
  none: 'No subscription',
  trialing: 'Trialing',
  active: 'Active',
  past_due: 'Payment past due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
};

/** How an organization came to exist. Empty means it predates recording. */
export const signupSourceLabel: Record<string, string> = {
  self_serve: 'Signed up',
  checkout: 'Bought on the website',
  '': 'Not recorded',
};

/** What a staff member did, in the staff console's activity list. */
export const staffActionLabel: Record<string, string> = {
  'staff.signed_in': 'Signed in',
  'staff.sign_in_failed': 'Sign-in refused',
  'organization.addon_granted': 'Add-on included',
  'organization.addon_removed': 'Add-on removed',
  'organization.suspended': 'Suspended',
  'organization.reactivated': 'Reactivated',
  'organization.licence_set': 'Store licence set',
  'organization.licence_revoked': 'Store licence revoked',
  'licence_plan.saved': 'Licence plan saved',
};

// ---------------------------------------------------------------------------
// WordPress / WooCommerce (CO-60)
// ---------------------------------------------------------------------------

/** Which updates an operator-started patch run may apply without a further
 * approval. Nothing is ever applied on a schedule. */
export const patchPolicyLabel: Record<string, string> = {
  manual_approval: 'Approve every update',
  security_only: 'Security updates run without approval',
  minor_and_security: 'Security, minor and bugfix updates run without approval',
  allowlist: 'Only listed plugins and themes run without approval',
};

export const patchPolicyDescription: Record<string, string> = {
  manual_approval: 'Every update in a patch run needs someone to approve it. The safest choice, and the default.',
  security_only:
    'Updates classified as security fixes may run without approval; everything else, and WordPress core, needs one.',
  minor_and_security:
    'Security, minor and bugfix updates found by WordPress’s own update check may run without approval. Major updates and WordPress core need one.',
  allowlist:
    'Non-major updates of the plugins and themes you list may run without approval; everything else, and WordPress core, needs one.',
};

/** Update categories. "Unknown" is shown as such — never guessed. */
export const updateCategoryLabel: Record<string, string> = {
  security: 'Security',
  major: 'Major',
  minor: 'Minor',
  bugfix: 'Bugfix',
  unknown: 'Not classified',
};

export const patchDecisionLabel: Record<string, string> = {
  allowed_by_policy: 'Covered by the policy',
  approval_required: 'Needs approval',
  blocked: 'Blocked',
};

export const compatibilityVerdictLabel: Record<string, string> = {
  compatible: 'Compatible',
  incompatible: 'Not compatible',
  unknown: 'Not declared',
};

export const securitySeverityLabel: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Information',
};

export const errorGroupStatusLabel: Record<string, string> = {
  open: 'Open',
  resolved: 'Resolved',
  muted: 'Muted',
};

export const errorCategoryLabel: Record<string, string> = {
  php_fatal: 'Fatal error',
  uncaught_exception: 'Uncaught exception',
  memory_exhausted: 'Out of memory',
  database: 'Database',
  checkout: 'Checkout',
  payment_integration: 'Payment integration',
  shipping_integration: 'Shipping integration',
  rest_api: 'REST API',
  cron: 'Cron',
  plugin: 'Plugin',
  theme: 'Theme',
  php_warning: 'Warning',
  application: 'Application',
};

// ---------------------------------------------------------------------------
// Status vocabulary (CO-87)
// ---------------------------------------------------------------------------

/**
 * The six semantic tones a status may have (styles.css --tone-*). Nothing
 * else decides what colour a status is: every badge, dot and callout for a
 * status goes through statusFor().
 *
 * - success: done, live, passing, reachable.
 * - warning: works but needs a look; live with failures; waiting on a person.
 * - danger: failed, broken, not possible, down.
 * - info: a neutral fact worth noticing (managed by Commerce Ops, approved).
 * - progress: something is running right now.
 * - neutral: nothing to report yet, not applicable, cancelled, not checked.
 *
 * Vocabulary for "we do not know" (docs/UX_TERMINOLOGY.md § Status words):
 * "Not checked yet" — the check has never produced a result;
 * "Unknown" — it tried and could not tell (e.g. the server is unreachable);
 * "Not monitored" — no check exists for it; "Not started yet" — a resource
 * that exists on paper but has not been started. These are different facts
 * and stay different words.
 */
export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'progress';

export interface StatusDef {
  label: string;
  tone: Tone;
}

export interface StatusPresentation extends StatusDef {
  kind: StatusKind;
  value: string;
}

const s_ = (label: string, tone: Tone): StatusDef => ({ label, tone });

/** Readiness / health level derived by projectHealth.ts. */
export type HealthLevel = 'ok' | 'warning' | 'critical' | 'unknown';

const levelStatus: Record<HealthLevel, StatusDef> = {
  ok: s_('Healthy', 'success'),
  warning: s_('Needs attention', 'warning'),
  critical: s_('Needs action now', 'danger'),
  unknown: s_('Unknown', 'neutral'),
};

const deploymentStatus: Record<string, StatusDef> = {
  pending: s_('Not started', 'neutral'),
  queued: s_('Queued', 'neutral'),
  preflighting: s_('Checking prerequisites', 'progress'),
  building: s_('Building', 'progress'),
  deploying: s_('Deploying', 'progress'),
  verifying: s_('Verifying', 'progress'),
  activating: s_('Switching traffic', 'progress'),
  post_deploy: s_('Finishing', 'progress'),
  // An outcome, not a serving state: four successive releases can all have
  // succeeded while only the newest serves (CO-150; see `serving`).
  completed: s_('Succeeded', 'success'),
  succeeded: s_('Succeeded', 'success'),
  completed_with_failures: s_('Partially succeeded', 'warning'),
  failed: s_('Failed', 'danger'),
  rolled_back: s_('Rolled back', 'warning'),
  cancelled: s_('Cancelled', 'neutral'),
  awaiting_db_approval: s_('Waiting for database approval', 'warning'),
};

/** Deployments of SSH (existing_atomic) environments. */
const legacyDeploymentStatus: Record<DeploymentStatus, StatusDef> = {
  queued: s_('Queued', 'neutral'),
  preparing: s_('Preparing', 'progress'),
  building: s_('Building', 'progress'),
  awaiting_db_approval: s_('Waiting for database approval', 'warning'),
  critical_window: s_('In the critical window', 'progress'),
  verifying: s_('Verifying', 'progress'),
  rolling_back: s_('Rolling back', 'progress'),
  succeeded: s_('Succeeded', 'success'),
  rolled_back: s_('Rolled back', 'warning'),
  recovery_required: s_('Needs recovery', 'danger'),
  failed: s_('Failed', 'danger'),
  cancelled: s_('Cancelled', 'neutral'),
};

const deploymentGroupStatus: Record<DeploymentGroupStatus, StatusDef> = {
  pending: s_('Not started', 'neutral'),
  running: s_('In progress', 'progress'),
  completed: s_('All live', 'success'),
  completed_with_failures: s_('Partly live', 'warning'),
  failed: s_('Failed', 'danger'),
  cancelled: s_('Cancelled', 'neutral'),
};

/**
 * Where a component stands after its newest finished deployment, from the
 * server's one derivation (domain.DeriveComponentStates, observations 2
 * and 4). Red only when nothing is live, or the failed release is.
 */
const componentOutcomeStatus: Record<string, StatusDef> = {
  live: s_('Live', 'success'),
  failed_previous_live: s_('Last deployment failed, previous release live', 'warning'),
  failed_not_live: s_('Last deployment failed', 'danger'),
  rolled_back: s_('Rolled back', 'warning'),
  partial: s_('Live, some checks failed', 'warning'),
  never_deployed: s_('Not deployed yet', 'neutral'),
};

/** One line for a Magento release's database change (observation 1 follow-up). */
const dbOverallStatus: Record<string, StatusDef> = {
  no_changes: s_('No database changes', 'neutral'),
  data_only: s_('Data changes only', 'neutral'),
  schema: s_('Schema changes', 'neutral'),
  schema_and_data: s_('Schema and data changes', 'neutral'),
  // Magento reports nothing pending; Commerce Ops' own release comparison
  // found changes. Worded like the list's "Database change" badge so the
  // two never read as a contradiction.
  flagged: s_('Database change flagged', 'neutral'),
  undetermined: s_('Not fully determined', 'warning'),
  pending: s_('Not analysed yet', 'neutral'),
};

/**
 * The three dimensions of an environment (CO-150), never collapsed into one
 * word: the live site (from its checks), what happened to an attempted
 * release, and whether that release serves now.
 */
const siteStateStatus: Record<string, StatusDef> = {
  operational: s_('Operational', 'success'),
  degraded: s_('Degraded', 'warning'),
  unavailable: s_('Unavailable', 'danger'),
  unknown: s_('Unknown', 'neutral'),
};

const alertChannelsStatus: Record<string, StatusDef> = {
  reachable: s_('Reaches you', 'success'),
  nobody: s_('Reaches nobody', 'danger'),
};

const deploymentOutcomeStatus: Record<string, StatusDef> = {
  queued: s_('Queued', 'neutral'),
  deploying: s_('Deploying', 'progress'),
  succeeded: s_('Succeeded', 'success'),
  succeeded_with_failures: s_('Partially succeeded', 'warning'),
  failed: s_('Failed', 'danger'),
  rolled_back: s_('Rolled back', 'warning'),
  cancelled: s_('Cancelled', 'neutral'),
  rehearsal: s_('Rehearsal', 'neutral'),
};

/**
 * What an operation did to production (UX_ARCHITECTURE.md, rule B): a third
 * dimension beside how the operation ended and how healthy the resource is.
 * "Not verified" is neutral, never green.
 */
const impactStatus: Record<string, StatusDef> = {
  none_observed: s_('No observed production impact', 'success'),
  detected: s_('Production impact detected', 'danger'),
  potential: s_('Potential production impact', 'warning'),
  not_verified: s_('Impact not verified', 'neutral'),
};

const servingStatus: Record<string, StatusDef> = {
  serving: s_('Serving now', 'success'),
  superseded: s_('Superseded', 'neutral'),
  never_activated: s_('Never activated', 'neutral'),
  rolled_back: s_('Switched back', 'neutral'),
  pending: s_('Not live yet', 'neutral'),
  unknown: s_('Serving not known', 'neutral'),
};

/** Infrastructure condition (domain.InfrastructureHealth.level). */
const infrastructureStatus: Record<string, StatusDef> = {
  ok: s_('Healthy', 'success'),
  warning: s_('Degraded', 'warning'),
  critical: s_('Critical', 'danger'),
  unknown: s_('Unknown', 'neutral'),
};

/** What one schema change does to existing data. */
const schemaImpactStatus: Record<string, StatusDef> = {
  none: s_('No effect', 'neutral'),
  additive: s_('Additive', 'info'),
  modifying: s_('Changes structure', 'warning'),
  destructive: s_('Drops data', 'danger'),
};

/**
 * What a component's visitors get now (blue-green runtimes). Serving health
 * is separate from deployment state: a rejected candidate never turns a
 * serving component red, and a protected backend is never "Serving".
 */
const runtimeServingStatus: Record<string, StatusDef> = {
  serving: s_('Serving', 'success'),
  // Routed, but no current health check of the component agrees: the route
  // record alone is not evidence that visitors are served.
  unverified: s_('Routed, not verified', 'neutral'),
  degraded: s_('Serving, a health check warns', 'warning'),
  unavailable: s_('Unavailable', 'danger'),
  protected: s_('Protected update in progress', 'warning'),
  manual_recovery: s_('Needs manual recovery', 'danger'),
  not_routed: s_('Recreate strategy', 'neutral'),
  none: s_('Not deployed yet', 'neutral'),
};

/** One blue-green runtime's lifecycle state. */
const runtimeStateStatus: Record<string, StatusDef> = {
  preparing: s_('Preparing', 'progress'),
  starting: s_('Starting', 'progress'),
  ready: s_('Verified, not routed', 'info'),
  promoting: s_('Promoting', 'progress'),
  serving: s_('Serving', 'success'),
  draining: s_('Running, no new traffic', 'warning'),
  drained: s_('Drained, still running', 'warning'),
  stopped: s_('Stopped, kept for rollback', 'neutral'),
  removed: s_('Removed', 'neutral'),
  manual_recovery: s_('Needs manual recovery', 'danger'),
};

/** The state of a server's Commerce Ops edge, as the agent read it. */
const edgeStatus: Record<string, StatusDef> = {
  running: s_('Running', 'success'),
  stopped: s_('Not running', 'danger'),
  not_installed: s_('Not installed yet', 'neutral'),
  per_environment: s_('Not in use: one gate per environment', 'neutral'),
  unused: s_('No environment uses it', 'neutral'),
  unknown: s_('Not read', 'neutral'),
};

/** What a finished coordinated deployment's components serve now (observation 10). */
const groupCurrentStatus: Record<string, StatusDef> = {
  all_live: s_('All live now', 'success'),
  not_all_live: s_('Not all live now', 'danger'),
};

/** Runs on the server (executions) and their steps. */
const executionStatus: Record<string, StatusDef> = {
  pending: s_('Not started', 'neutral'),
  ready: s_('Ready to start', 'neutral'),
  preflighting: s_('Checking prerequisites', 'progress'),
  running: s_('Running', 'progress'),
  paused: s_('Paused', 'warning'),
  paused_waiting_for_agent: s_('Waiting for the agent to reconnect', 'warning'),
  // The outcome of a step is unknown; the run holds the environment until
  // the operator settles it (RES-001).
  waiting_for_input: s_('Needs you', 'danger'),
  blocked: s_('Blocked', 'danger'),
  // A run that completed and a step that succeeded are one outcome.
  completed: s_('Succeeded', 'success'),
  succeeded: s_('Succeeded', 'success'),
  skipped: s_('Skipped', 'neutral'),
  failed: s_('Failed', 'danger'),
  cancelled: s_('Cancelled', 'neutral'),
  rolled_back: s_('Rolled back', 'warning'),
};

/** A deployment stage: up to 14 per run, each grouping its steps. */
const stageStatus: Record<string, StatusDef> = {
  pending: s_('Not started', 'neutral'),
  running: s_('Running', 'progress'),
  succeeded: s_('Succeeded', 'success'),
  warning: s_('Succeeded with warnings', 'warning'),
  failed: s_('Failed', 'danger'),
  skipped: s_('Not needed', 'neutral'),
  cancelled: s_('Cancelled', 'neutral'),
  not_run: s_('Not run', 'neutral'),
};

const jobStatus: Record<JobStatus, StatusDef> = {
  queued: s_('Queued', 'neutral'),
  leased: s_('Running', 'progress'),
  succeeded: s_('Finished', 'success'),
  failed: s_('Did not finish cleanly', 'danger'),
};

const planLifecycleStatus: Record<string, StatusDef> = {
  draft: s_('Draft', 'neutral'),
  needs_input: s_('Needs information', 'warning'),
  blocked: s_('Not possible on this server', 'danger'),
  awaiting_approval: s_('Awaiting approval', 'warning'),
  approved: s_('Approved — not run yet', 'info'),
  run_pending: s_('Run created — not started', 'info'),
  running: s_('Running', 'progress'),
  completed: s_('Completed', 'success'),
  failed: s_('Failed', 'danger'),
  cancelled: s_('Cancelled', 'neutral'),
  superseded: s_('Superseded', 'neutral'),
};

/** A continuous check (monitor). */
const checkStatus: Record<ContinuousMonitorStatus, StatusDef> = {
  healthy: s_('Passing', 'success'),
  warning: s_('Warning', 'warning'),
  critical: s_('Failing', 'danger'),
  unknown: s_('Not checked yet', 'neutral'),
  disabled: s_('Monitoring paused', 'neutral'),
};

const componentHealthStatus: Record<string, StatusDef> = {
  healthy: s_('Healthy', 'success'),
  warning: s_('Needs attention', 'warning'),
  critical: s_('Down', 'danger'),
  unknown: s_('Not checked yet', 'neutral'),
  disabled: s_('Disabled', 'neutral'),
  not_monitored: s_('Not monitored', 'neutral'),
};

/** A Magento website, store view or the backend (topology health). */
const siteHealthStatus: Record<string, StatusDef> = {
  healthy: s_('Healthy', 'success'),
  warning: s_('Needs attention', 'warning'),
  critical: s_('Failing', 'danger'),
  unknown: s_('Not checked yet', 'neutral'),
  disabled: s_('Monitoring paused', 'neutral'),
};

const serverAgentStatus: Record<ServerStatus, StatusDef> = {
  online: s_('Agent connected', 'success'),
  offline: s_('Agent offline', 'danger'),
  waiting_for_agent: s_('Waiting for agent', 'warning'),
  error: s_('Error', 'danger'),
};

const serverCheckStatus: Record<ServerReadinessStatus, StatusDef> = {
  not_run: s_('Not checked yet', 'neutral'),
  running: s_('Checking…', 'progress'),
  ready: s_('Ready', 'success'),
  warning: s_('Ready with warnings', 'warning'),
  failed: s_('Not ready', 'danger'),
};

/** One row of a server check. */
const serverCheckRowStatus: Record<ReadinessCheckStatus, StatusDef> = {
  pass: s_('Passed', 'success'),
  warning: s_('Warning', 'warning'),
  fail: s_('Failed', 'danger'),
  info: s_('Information', 'info'),
};

const capabilityStatus: Record<CapabilityStatus, StatusDef> = {
  recommended: s_('Recommended', 'success'),
  available: s_('Available', 'success'),
  supported: s_('Supported', 'success'),
  supported_with_warning: s_('Supported with warnings', 'warning'),
  blocked: s_('Not possible on this server', 'danger'),
  unknown: s_('Not detected', 'neutral'),
};

const capabilityDetectionStatus: Record<CapabilityDetectionStatus | 'detected_low_confidence', StatusDef> = {
  detected: s_('Detected', 'success'),
  detected_low_confidence: s_('Detected, low confidence', 'warning'),
  unresolved: s_('Not determined', 'warning'),
  conflict: s_('Store and repository disagree', 'danger'),
};

const ownershipStatus: Record<ResourceOwnership, StatusDef> = {
  commerceops: s_('Managed by Commerce Ops', 'info'),
  external: s_('External', 'neutral'),
  detected: s_('Found on the server', 'neutral'),
  unknown: s_('Unknown', 'neutral'),
};

const resourceStatus: Record<ResourceStatus, StatusDef> = {
  reachable: s_('Reachable', 'success'),
  unreachable: s_('Unreachable', 'danger'),
  pending: s_('Not started yet', 'neutral'),
  unknown: s_('Unknown', 'neutral'),
};

/** Varnish full page cache of an environment (CO-127, EnvironmentVarnish.state). */
const varnishStateStatus: Record<VarnishState, StatusDef> = {
  active: s_('Active', 'success'),
  awaiting_first_release: s_('Starts with the first deployment', 'neutral'),
  waiting_for_agent: s_('Waiting for the agent', 'warning'),
  planned: s_('Planned', 'info'),
  not_planned: s_('Not set up', 'neutral'),
  not_provisioned_here: s_('Not used here', 'neutral'),
  not_applicable: s_('Not part of this topology', 'neutral'),
};

const recoveryPointStatus: Record<RecoveryPointStatus, StatusDef> = {
  creating: s_('Creating', 'progress'),
  uploading: s_('Uploading', 'progress'),
  verifying: s_('Verifying', 'progress'),
  // The file exists. Whether it restores is the restore test's verdict, shown beside it.
  ready: s_('Stored', 'neutral'),
  verification_failed: s_('Verification failed', 'danger'),
  failed: s_('Failed', 'danger'),
  expired: s_('Expired', 'neutral'),
  deleting: s_('Deleting', 'progress'),
  deleted: s_('Deleted', 'neutral'),
};

const verificationStatus: Record<VerificationStatus, StatusDef> = {
  unverified: s_('Not verified', 'neutral'),
  checksum_verified: s_('Checksum verified', 'success'),
  verification_failed: s_('Verification failed', 'danger'),
};

const restoreTestStatus: Record<RestoreTestStatus, StatusDef> = {
  not_tested: s_('Restore not tested', 'neutral'),
  testing: s_('Testing restore', 'progress'),
  restore_verified: s_('Restorable (tested)', 'success'),
  restore_failed: s_('Restore test failed', 'danger'),
};

/** Where a recovery point's copies are (RES-039). */
const backupCopiesStatus: Record<string, StatusDef> = {
  both: s_('On the server and off-host', 'success'),
  off_host_only: s_('Off-host only', 'info'),
  local_only: s_('Only on the server', 'warning'),
  lost: s_('No copy left', 'danger'),
  pending: s_('Not stored yet', 'neutral'),
};

/** What an ended restore did to the live store (RES-041). */
const restoreOutcomeStatus: Record<string, StatusDef> = {
  restored: s_('Restored', 'success'),
  untouched: s_('Live store unchanged', 'neutral'),
  database_partially_replaced: s_('Database partially replaced', 'danger'),
  media_partially_extracted: s_('Media partially restored', 'danger'),
  restored_with_errors: s_('Restored with errors', 'warning'),
};

const restoreRunStatus: Record<RestoreRunStatus, StatusDef> = {
  pending: s_('Not started', 'neutral'),
  preflighting: s_('Checking prerequisites', 'progress'),
  protecting_current: s_('Backing up the current state', 'progress'),
  restoring_db: s_('Restoring the database', 'progress'),
  restoring_media: s_('Restoring media', 'progress'),
  verifying: s_('Verifying', 'progress'),
  completed: s_('Restored', 'success'),
  failed: s_('Failed', 'danger'),
};

const subscriptionStatus: Record<SubscriptionStatus, StatusDef> = {
  none: s_('No subscription', 'neutral'),
  trialing: s_('Trialing', 'info'),
  active: s_('Active', 'success'),
  past_due: s_('Payment past due', 'warning'),
  canceled: s_('Canceled', 'neutral'),
  incomplete: s_('Incomplete', 'warning'),
};

const incidentStatus: Record<IncidentLifecycleStatus, StatusDef> = {
  open: s_('Open', 'danger'),
  acknowledged: s_('Acknowledged', 'warning'),
  resolved: s_('Resolved', 'success'),
  suppressed: s_('Suppressed', 'neutral'),
};

/** Severity of incidents, alerts, findings and vulnerabilities. */
const severityStatus: Record<string, StatusDef> = {
  critical: s_('Critical', 'danger'),
  high: s_('High', 'danger'),
  error: s_('Error', 'danger'),
  warning: s_('Warning', 'warning'),
  medium: s_('Medium', 'warning'),
  low: s_('Low', 'neutral'),
  info: s_('Information', 'info'),
};

const riskStatus: Record<RiskLevel, StatusDef> = {
  critical: s_('Critical risk', 'danger'),
  high: s_('High risk', 'danger'),
  medium: s_('Medium risk', 'warning'),
  low: s_('Low risk', 'neutral'),
  info: s_('Information', 'info'),
  unknown: s_('Risk unknown', 'warning'),
};

const preflightStatus: Record<PreflightStatus, StatusDef> = {
  pending: s_('Not checked yet', 'neutral'),
  passed: s_('Passed', 'success'),
  warning: s_('Warning', 'warning'),
  awaiting_approval: s_('Awaiting approval', 'warning'),
  approved: s_('Approved', 'info'),
  rejected: s_('Rejected', 'danger'),
  failed: s_('Failed', 'danger'),
  no_upgrade_required: s_('No upgrade needed', 'success'),
};

/**
 * A database change is an impact characteristic of a release, not a
 * problem (CO-150): plain changes are informational; only changes that drop
 * data or could not be analysed ask for a look.
 */
const dbChangeStatus: Record<string, StatusDef> = {
  no_db_change: s_('No database change', 'neutral'),
  db_change_backward_compatible: s_('Compatible database change', 'neutral'),
  db_change_detected: s_('Database change', 'neutral'),
  db_change_destructive: s_('Destructive database change', 'warning'),
  db_change_high_risk: s_('High-risk database change', 'warning'),
  db_change_unknown: s_('Database change not analysed', 'neutral'),
  unknown: s_('Not analysed', 'neutral'),
};

const dnsStatus: Record<string, StatusDef> = {
  ok: s_('Points at the server', 'success'),
  missing: s_('No DNS record', 'danger'),
  mismatch: s_('Points elsewhere', 'danger'),
  proxied: s_('Through a CDN', 'success'),
  unknown: s_('Not checked yet', 'neutral'),
};

const tlsStatus: Record<string, StatusDef> = {
  valid: s_('Valid certificate', 'success'),
  invalid: s_('Certificate not trusted', 'danger'),
  expired: s_('Certificate expired', 'danger'),
  hostname_mismatch: s_('Certificate for another hostname', 'danger'),
  missing: s_('No HTTPS', 'danger'),
  not_applicable: s_('HTTP only', 'neutral'),
  unknown: s_('Not checked yet', 'neutral'),
};

const certificateStatus: Record<CertificateStatus | 'staging', StatusDef> = {
  none: s_('No certificate', 'neutral'),
  // A staging certificate is installed: browsers reject it, so it is never "Issued".
  staging: s_('Staging, not trusted', 'warning'),
  placeholder: s_('Placeholder certificate', 'warning'),
  issued: s_('Issued', 'success'),
  issue_failed: s_('Issue failed', 'danger'),
  renewal_failed: s_('Renewal failed', 'danger'),
  expired: s_('Expired', 'danger'),
};

const autoIssueStatus: Record<AutoIssueStatus, StatusDef> = {
  off: s_('Off', 'neutral'),
  idle: s_('Up to date', 'success'),
  waiting_dns: s_('Waiting for DNS', 'warning'),
  issuing: s_('Issuing', 'info'),
  backoff: s_('Retrying later', 'warning'),
  rate_limited: s_('Rate limited', 'warning'),
  paused: s_('Paused', 'warning'),
  not_applicable: s_('Not applicable', 'neutral'),
};

const originCertificateStatus: Record<string, StatusDef> = {
  valid: s_('Valid', 'success'),
  expired: s_('Expired', 'danger'),
  placeholder: s_('Placeholder', 'warning'),
  staging: s_('Staging, not trusted', 'warning'),
  missing: s_('Missing', 'danger'),
  hostname_mismatch: s_('Other hostname', 'danger'),
  unknown: s_('Not read', 'neutral'),
};

const domainVerificationStatus: Record<string, StatusDef> = {
  passed: s_('Verified', 'success'),
  failed: s_('Failing', 'danger'),
  warning: s_('Needs attention', 'warning'),
  unknown: s_('Not verified yet', 'neutral'),
};

const componentLifecycleStatus: Record<string, StatusDef> = {
  active: s_('Active', 'success'),
  removing: s_('Removing…', 'progress'),
  removal_failed: s_('Removal failed', 'danger'),
  removed: s_('Removed', 'neutral'),
};

const updateCategoryStatus: Record<string, StatusDef> = {
  security: s_('Security', 'danger'),
  major: s_('Major', 'warning'),
  minor: s_('Minor', 'neutral'),
  bugfix: s_('Bugfix', 'neutral'),
  unknown: s_('Not classified', 'neutral'),
};

const errorGroupStatus: Record<ErrorGroupStatus, StatusDef> = {
  open: s_('Open', 'danger'),
  resolved: s_('Resolved', 'success'),
  muted: s_('Muted', 'neutral'),
};

const storeMigrationStatus: Record<StoreMigrationStatus, StatusDef> = {
  assessing: s_('Checking the files', 'progress'),
  assessed: s_('Ready to import', 'info'),
  blocked: s_('Blocked', 'danger'),
  approved: s_('Approved', 'info'),
  importing: s_('Importing', 'progress'),
  imported: s_('Imported', 'success'),
  failed: s_('Failed', 'danger'),
};

const invitationStatus: Record<InvitationStatus, StatusDef> = {
  pending: s_('Pending', 'info'),
  accepted: s_('Accepted', 'success'),
  revoked: s_('Revoked', 'neutral'),
  expired: s_('Expired', 'neutral'),
};

/** The plan step's position relative to the production-changing window. */
const safetyBoundaryStatus: Record<string, StatusDef> = {
  before_critical_window: s_('Before the critical window', 'neutral'),
  critical_window: s_('Critical window', 'warning'),
  post_deploy: s_('After deployment', 'success'),
};

/** What the setup plan does with one component. */
const planActionStatus: Record<string, StatusDef> = {
  reuse: s_('Reuse what is there', 'neutral'),
  create: s_('Commerce Ops creates it', 'neutral'),
  configure: s_('Commerce Ops configures it', 'neutral'),
  integrate: s_('Connect to it, leave it unchanged', 'neutral'),
  leave_unchanged: s_('Leave unchanged', 'neutral'),
  requires_user_input: s_('Needs your decision', 'warning'),
  blocked: s_('Not possible', 'danger'),
};

/** A risk the setup plan (or one of its steps) calls out. */
const planRiskStatus: Record<string, StatusDef> = {
  info: s_('Note', 'info'),
  warning: s_('Warning', 'warning'),
  high: s_('High risk', 'danger'),
  blocking: s_('Blocks the plan', 'danger'),
};

/** Where the plan's knowledge of the repository came from. */
const repositoryAnalysisStatus: Record<string, StatusDef> = {
  repository: s_('Analyzed from repository', 'success'),
  assumed_defaults: s_('Using assumed defaults', 'warning'),
  not_magento: s_('Not a Magento repository', 'danger'),
};

/** A project's or environment's one word, as the server derives it (domain.HealthLevel, CO-95). */
const projectHealthStatus: Record<string, StatusDef> = {
  healthy: s_('Healthy', 'success'),
  degraded: s_('Needs attention', 'warning'),
  critical: s_('Needs action now', 'danger'),
  unknown: s_('Unknown', 'neutral'),
};

/** Whether Commerce Ops can read a project's repositories. */
const repositoryAccessStatus: Record<string, StatusDef> = {
  configured: s_('Configured', 'success'),
  works: s_('Access works', 'success'),
  failed: s_('Access failed', 'danger'),
  not_configured: s_('Not configured', 'neutral'),
  // Which credential reads one repository (observation 9).
  github_app: s_('GitHub App', 'success'),
  project_connection: s_('Project token', 'success'),
  none: s_('No credential', 'warning'),
  optional: s_('Optional', 'neutral'),
};

/** Whether an environment variable has what it needs. */
const variableValueStatus: Record<string, StatusDef> = {
  secret_stored: s_('Secret stored', 'success'),
  missing_secret: s_('No secret value', 'warning'),
  missing: s_('No value', 'warning'),
};

/** A personal access token (MCP): usable, close to expiry, or past it. */
const accessTokenStatus: Record<string, StatusDef> = {
  active: s_('Active', 'success'),
  expiring: s_('Expires soon', 'warning'),
  expired: s_('Expired', 'neutral'),
};

const emailVerificationStatus: Record<string, StatusDef> = {
  verified: s_('Verified', 'success'),
  not_verified: s_('Not verified', 'neutral'),
};

/** One check of a deployment's verification or impact analysis. */
const checkResultStatus: Record<string, StatusDef> = {
  passed: s_('Passed', 'success'),
  warning: s_('Warning', 'warning'),
  failed: s_('Failed', 'danger'),
  skipped: s_('Skipped', 'neutral'),
};

/** The speed check of a deployment: the storefront's answer time before and after (CO-198). */
const speedCheckStatus: Record<string, StatusDef> = {
  ok: s_('Not slower', 'success'),
  slower: s_('Slower', 'warning'),
  measuring: s_('Measuring', 'neutral'),
  unknown: s_('Not enough checks', 'neutral'),
};

/** What a deployment does to one cache layer (CO-46, CO-201): from the component's cache configuration, never assumed. */
const cacheActionStatus: Record<string, StatusDef> = {
  flushed_by_deployment: s_('Flushed', 'success'),
  recreated_by_deployment: s_('Rebuilt', 'success'),
  purged_by_deployment: s_('Purged automatically', 'success'),
  operator_purge_required: s_('Operator purge required', 'warning'),
};

/**
 * The stored Cloudflare token of a component after a check (CO-201). Valid
 * is a token Cloudflare accepts that reads the zone; whether it may purge
 * is not testable without purging, so the word never says it.
 */
const cloudflareTokenStatus: Record<string, StatusDef> = {
  not_configured: s_('Token missing', 'neutral'),
  invalid: s_('Token invalid', 'danger'),
  insufficient_scope: s_('Zone not readable', 'warning'),
  zone_not_found: s_('Zone not found', 'danger'),
  valid: s_('Valid, scope unverified', 'success'),
  unknown: s_('Not verified', 'neutral'),
};

/**
 * One permission of the Cloudflare token (CO-201 phase 3). Cache Purge is
 * never anything but untestable: Cloudflare cannot be asked without purging.
 * A required permission that is missing is a problem; an optional one only
 * leaves a column of the zone view empty.
 */
const cloudflarePermissionStatus: Record<string, StatusDef> = {
  granted: s_('Granted', 'success'),
  missing_required: s_('Missing', 'danger'),
  missing: s_('Not granted', 'warning'),
  unknown: s_('Unknown', 'neutral'),
  untestable: s_('Not testable', 'neutral'),
};

/** A personalised route as the edge treated it (cache safety check). */
const cacheRouteStatus: Record<string, StatusDef> = {
  safe: s_('Not cached', 'success'),
  cacheable: s_('Storable', 'warning'),
  cached: s_('Cached', 'danger'),
  unknown: s_('Unknown', 'neutral'),
  absent: s_('Not found', 'neutral'),
};

/** The whole cache safety check; unknown is never shown as safe. */
const cacheSafetyStatus: Record<string, StatusDef> = {
  safe: s_('Not cached', 'success'),
  warning: s_('Storable', 'warning'),
  unsafe: s_('Cached', 'danger'),
  unknown: s_('Unknown', 'neutral'),
};

/** Whether this installation implements a capability. */
const implementationStatus: Record<string, StatusDef> = {
  implemented: s_('Available', 'success'),
  not_implemented: s_('Not available', 'neutral'),
  disabled: s_('Turned off', 'neutral'),
};

const entitlementStatus: Record<string, StatusDef> = {
  entitled: s_('Entitled', 'success'),
  not_entitled: s_('Not entitled', 'neutral'),
};

/** A Magento website or store view as Commerce Ops last saw it. */
const topologyItemStatus: Record<string, StatusDef> = {
  active: s_('Active', 'success'),
  disabled: s_('Disabled', 'neutral'),
  removed: s_('No longer in Magento', 'danger'),
};

/** Whether an organization may use Commerce Ops (staff console). */
const organizationAccessStatus: Record<string, StatusDef> = {
  active: s_('Active', 'success'),
  suspended: s_('Suspended', 'danger'),
};

/** An add-on for one organization (staff console). */
const addonStatus: Record<string, StatusDef> = {
  included: s_('Included', 'success'),
  in_use: s_('In use', 'neutral'),
  in_use_not_included: s_('In use, not included', 'warning'),
};

/** What a staff member did (staff console activity). */
const staffActionStatus: Record<string, StatusDef> = {
  'staff.signed_in': s_('Signed in', 'neutral'),
  'staff.sign_in_failed': s_('Sign-in refused', 'warning'),
  'organization.addon_granted': s_('Add-on included', 'success'),
  'organization.addon_removed': s_('Add-on removed', 'neutral'),
  'organization.suspended': s_('Suspended', 'danger'),
  'organization.reactivated': s_('Reactivated', 'success'),
  'organization.licence_set': s_('Licence set', 'success'),
  'organization.licence_revoked': s_('Licence revoked', 'danger'),
  'licence_plan.saved': s_('Plan saved', 'neutral'),
};

/** A store licence (CO-143). */
const licenceStatus: Record<string, StatusDef> = {
  active: s_('Active', 'success'),
  grace: s_('Grace period', 'warning'),
  expired: s_('Not licensed', 'danger'),
  none: s_('No plan', 'neutral'),
  domain_not_licensed: s_('Over the domain limit', 'warning'),
};

/** A production domain against the plan's domain limit. */
const licensedDomainStatus: Record<string, StatusDef> = {
  active: s_('Licensed', 'success'),
  pending: s_('Waiting for a free slot', 'warning'),
  released: s_('Released', 'neutral'),
};

/** Magento integration monitors (SSH environments). */
const integrationMonitorStatus: Record<string, StatusDef> = {
  healthy: s_('Healthy', 'success'),
  delayed: s_('Delayed', 'warning'),
  failing: s_('Failing', 'danger'),
  stale: s_('Stale data', 'warning'),
  idle: s_('Idle', 'neutral'),
  success: s_('Succeeded', 'success'),
  warning: s_('Warning', 'warning'),
  failed: s_('Failed', 'danger'),
  running: s_('Running', 'progress'),
};

/** One result of an audit or check list (server audit, config audit, CDN). */
const auditResultStatus: Record<string, StatusDef> = {
  pass: s_('Passed', 'success'),
  passed: s_('Passed', 'success'),
  warning: s_('Warning', 'warning'),
  warn: s_('Warning', 'warning'),
  fail: s_('Failed', 'danger'),
  failed: s_('Failed', 'danger'),
  critical: s_('Critical', 'danger'),
  incompatible: s_('Incompatible', 'danger'),
  compatible: s_('Compatible', 'success'),
  info: s_('Information', 'info'),
  skipped: s_('Skipped', 'neutral'),
};

/** The overall rating of a Magento security sweep. */
const securityRatingStatus: Record<string, StatusDef> = {
  secure: s_('Secure', 'success'),
  needs_attention: s_('Needs attention', 'warning'),
  at_risk: s_('At risk', 'danger'),
  critical: s_('Critical', 'danger'),
};

/** How ready a storefront is to sit behind a CDN. */
const cdnReadinessStatus: Record<string, StatusDef> = {
  optimal: s_('Ready', 'success'),
  needs_tuning: s_('Needs tuning', 'warning'),
  unoptimized: s_('Not ready', 'danger'),
};

/** A live connection (terminal, SSH). */
const connectionStatus: Record<string, StatusDef> = {
  connected: s_('Connected', 'success'),
  connecting: s_('Connecting…', 'progress'),
  disconnected: s_('Disconnected', 'neutral'),
  error: s_('Error', 'danger'),
  configured: s_('Configured', 'success'),
  works: s_('Connection works', 'success'),
  failed: s_('Connection failed', 'danger'),
  not_configured: s_('Not configured', 'neutral'),
  not_connected: s_('Not connected', 'neutral'),
  installed: s_('Installed', 'success'),
  awaiting_installation: s_('Waiting to be installed', 'warning'),
  per_project: s_('Per project', 'neutral'),
};

/** A setting that is on or off. */
const toggleStatus: Record<string, StatusDef> = {
  enabled: s_('Enabled', 'success'),
  disabled: s_('Disabled', 'neutral'),
};

/** Where a store product stands against the ERP's articles (CO-155). */
const matchStateStatus: Record<string, StatusDef> = {
  matched: s_('Matched', 'success'),
  no_code: s_('Needs a match', 'neutral'),
  unmatched: s_('Code not found', 'warning'),
  conflict: s_('In two websites', 'danger'),
};

/** Where a request to save a match in the store stands (CO-155). */
const matchRequestStatus: Record<string, StatusDef> = {
  pending: s_('Saving to the store', 'info'),
  rejected: s_('Not saved', 'danger'),
  expired: s_('Not confirmed', 'warning'),
};

/** One row of a batch review or an imported file (CO-162, CO-170). */
const matchRowStatus: Record<string, StatusDef> = {
  ready: s_('Ready', 'success'),
  unchanged: s_('Already matched', 'neutral'),
  replace: s_('Replaces a match', 'warning'),
  error: s_('Not imported', 'danger'),
};

/**
 * What a suggested ERP article is based on, as facts (CO-170). Never a
 * verdict: there is no "sure", because an equal code is evidence, not proof.
 */
const matchEvidenceStatus: Record<string, StatusDef> = {
  same_code: s_('Code matches', 'info'),
  same_code_loose: s_('Same code apart from leading zeros', 'neutral'),
  barcode_is_sku: s_('Barcode matches', 'info'),
  sku_in_name: s_('SKU in the name', 'neutral'),
  similar_name: s_('Similar name', 'neutral'),
};

/** Whether the product's name and the article's name describe the same thing (CO-170). */
const matchNamesStatus: Record<string, StatusDef> = {
  agree: s_('Names agree', 'success'),
  partial: s_('Names partly agree', 'neutral'),
  differ: s_('Names differ', 'warning'),
  unknown: s_('Names not comparable', 'neutral'),
};

/** A backup or restore artefact (SSH environments). */
const backupStatus: Record<string, StatusDef> = {
  completed: s_('Completed', 'success'),
  failed: s_('Failed', 'danger'),
  // "Verify restore" loads the backup into a throwaway copy; the live data is untouched.
  restored: s_('Restore verified (test copy)', 'success'),
  extracted: s_('Extraction verified (test copy)', 'success'),
  reconciled: s_('Reconciled', 'success'),
  simulated: s_('Simulated', 'neutral'),
};

/** How a file differs in a patch. */
const gitChangeStatus: Record<string, StatusDef> = {
  added: s_('Added', 'success'),
  modified: s_('Modified', 'neutral'),
  untracked: s_('Untracked', 'neutral'),
  unknown: s_('Unknown', 'neutral'),
};

/** A Magento cron job or consumer (SSH environments). */
const processStatus: Record<string, StatusDef> = {
  running: s_('Running', 'success'),
  success: s_('Succeeded', 'success'),
  pending: s_('Scheduled', 'neutral'),
  missed: s_('Missed', 'warning'),
  error: s_('Error', 'danger'),
  stopped: s_('Stopped', 'neutral'),
  idle: s_('Idle', 'neutral'),
};

/** A Magento indexer (SSH environments). */
const magentoIndexerStatus: Record<string, StatusDef> = {
  ready: s_('Ready', 'success'),
  reindex_required: s_('Reindex required', 'warning'),
  working: s_('Reindexing', 'progress'),
  unknown: s_('Not verified', 'neutral'),
};

/** A server Commerce Ops created at the hosting provider. */
const managedServerStatus: Record<string, StatusDef> = {
  provisioning: s_('Provisioning', 'progress'),
  running: s_('Running', 'success'),
  failed: s_('Failed', 'danger'),
  deleted: s_('Deleted', 'neutral'),
};

/** Whether a response can be kept by a CDN. */
const cacheabilityStatus: Record<string, StatusDef> = {
  cacheable: s_('Cacheable', 'success'),
  private: s_('Private, not cached', 'warning'),
};

/** An automatic fix Commerce Ops proposed or ran (monitoring). */
const remediationActionStatus: Record<RemediationActionState, StatusDef> = {
  proposed: s_('Awaiting approval', 'warning'),
  waiting_for_approval: s_('Awaiting approval', 'warning'),
  approved: s_('Approved', 'info'),
  executing: s_('Running', 'progress'),
  verifying: s_('Verifying', 'progress'),
  succeeded: s_('Succeeded', 'success'),
  failed: s_('Failed', 'danger'),
  suppressed: s_('Suppressed', 'neutral'),
  blocked: s_('Blocked', 'danger'),
  escalated: s_('Stopped by the circuit breaker', 'danger'),
  cancelled: s_('Cancelled', 'neutral'),
};

const remediationRiskStatus: Record<RemediationRisk, StatusDef> = {
  low: s_('Low risk', 'neutral'),
  moderate: s_('Medium risk', 'warning'),
  high: s_('High risk', 'danger'),
  prohibited_auto: s_('Manual only', 'warning'),
};

export const statusTables = {
  varnishState: varnishStateStatus,
  remediationAction: remediationActionStatus,
  remediationRisk: remediationRiskStatus,
  magentoIndexer: magentoIndexerStatus,
  managedServer: managedServerStatus,
  cacheability: cacheabilityStatus,
  integrationMonitor: integrationMonitorStatus,
  auditResult: auditResultStatus,
  securityRating: securityRatingStatus,
  cdnReadiness: cdnReadinessStatus,
  connection: connectionStatus,
  toggle: toggleStatus,
  matchState: matchStateStatus,
  matchRequest: matchRequestStatus,
  matchRow: matchRowStatus,
  matchEvidence: matchEvidenceStatus,
  matchNames: matchNamesStatus,
  backup: backupStatus,
  gitChange: gitChangeStatus,
  process: processStatus,
  topologyItem: topologyItemStatus,
  organizationAccess: organizationAccessStatus,
  addon: addonStatus,
  staffAction: staffActionStatus,
  licence: licenceStatus,
  licensedDomain: licensedDomainStatus,
  projectHealth: projectHealthStatus,
  repositoryAccess: repositoryAccessStatus,
  variableValue: variableValueStatus,
  emailVerification: emailVerificationStatus,
  accessToken: accessTokenStatus,
  checkResult: checkResultStatus,
  speedCheck: speedCheckStatus,
  cacheAction: cacheActionStatus,
  cloudflareToken: cloudflareTokenStatus,
  cloudflarePermission: cloudflarePermissionStatus,
  cacheRoute: cacheRouteStatus,
  cacheSafety: cacheSafetyStatus,
  implementation: implementationStatus,
  entitlement: entitlementStatus,
  planAction: planActionStatus,
  planRisk: planRiskStatus,
  repositoryAnalysis: repositoryAnalysisStatus,
  level: levelStatus,
  siteState: siteStateStatus,
  deploymentOutcome: deploymentOutcomeStatus,
  serving: servingStatus,
  impact: impactStatus,
  infrastructure: infrastructureStatus,
  deployment: deploymentStatus,
  legacyDeployment: legacyDeploymentStatus,
  deploymentGroup: deploymentGroupStatus,
  componentOutcome: componentOutcomeStatus,
  runtimeServing: runtimeServingStatus,
  runtimeState: runtimeStateStatus,
  groupCurrent: groupCurrentStatus,
  dbOverall: dbOverallStatus,
  schemaImpact: schemaImpactStatus,
  edge: edgeStatus,
  execution: executionStatus,
  stage: stageStatus,
  job: jobStatus,
  planLifecycle: planLifecycleStatus,
  check: checkStatus,
  componentHealth: componentHealthStatus,
  siteHealth: siteHealthStatus,
  alertChannels: alertChannelsStatus,
  serverAgent: serverAgentStatus,
  serverCheck: serverCheckStatus,
  serverCheckRow: serverCheckRowStatus,
  capability: capabilityStatus,
  capabilityDetection: capabilityDetectionStatus,
  ownership: ownershipStatus,
  resource: resourceStatus,
  recoveryPoint: recoveryPointStatus,
  verification: verificationStatus,
  restoreTest: restoreTestStatus,
  restoreRun: restoreRunStatus,
  restoreOutcome: restoreOutcomeStatus,
  backupCopies: backupCopiesStatus,
  subscription: subscriptionStatus,
  incident: incidentStatus,
  severity: severityStatus,
  risk: riskStatus,
  preflight: preflightStatus,
  dbChange: dbChangeStatus,
  dns: dnsStatus,
  tls: tlsStatus,
  certificate: certificateStatus,
  autoIssue: autoIssueStatus,
  originCertificate: originCertificateStatus,
  domainVerification: domainVerificationStatus,
  componentLifecycle: componentLifecycleStatus,
  updateCategory: updateCategoryStatus,
  errorGroup: errorGroupStatus,
  storeMigration: storeMigrationStatus,
  invitation: invitationStatus,
  safetyBoundary: safetyBoundaryStatus,
} satisfies Record<string, Record<string, StatusDef>>;

export type StatusKind = keyof typeof statusTables;

/** Every value each kind knows, for tests and the UI gallery. */
export const statusValues = Object.fromEntries(
  Object.entries(statusTables).map(([kind, table]) => [kind, Object.keys(table)]),
) as Record<StatusKind, string[]>;

/**
 * statusFor is the one place a status becomes words and a tone. An unknown
 * value (a new API state the dashboard does not know yet) is shown
 * humanized and neutral — never guessed as good or bad.
 */
export function statusFor(kind: StatusKind, value: string | null | undefined): StatusPresentation {
  const table = statusTables[kind] as Record<string, StatusDef>;
  const v = value ?? '';
  const def = table[v];
  if (def) return { ...def, kind, value: v };
  return { label: v ? humanize(v) : statusTables.level.unknown.label, tone: 'neutral', kind, value: v };
}

/** The tone of a readiness level, for dots and callouts that are not badges. */
export function levelTone(level: HealthLevel): Tone {
  return levelStatus[level].tone;
}

/** What a store extension sends Commerce Ops, by entity (CO-143). */
export const dataEntityLabel: Record<string, string> = {
  order: 'Orders',
  invoice: 'Invoices',
  credit_memo: 'Credit memos',
  customer: 'Customers',
  search_query: 'Search queries',
};

/** Why a store licence is not plainly active, in the owner's words. */
export const licenceReasonText: Record<string, string> = {
  no_plan: 'The organization has no plan yet.',
  plan_inactive: 'The plan is no longer offered.',
  subscription_lapsed: 'The subscription is not paid.',
  licence_period_ended: 'The licence period ended.',
  staff_revoked: 'Hestaworks revoked the licence.',
  organization_suspended: 'The organization is suspended.',
  domain_limit_reached: 'Your stores run more shop websites than the plan allows.',
};
